import type { I18nLocale } from "@/modules/i18n/types"
import type {
  WoosMapLocalitiesAutocomplete,
  WoosMapLocalityDetailResult,
  WoosMapLocalityTypes,
} from "@/modules/woosmap/types"

import { getResultFromResponse } from "@/modules/apiRequests/handlers"
import { handleFetchError } from "@/modules/fetch/errorHandler"
import { fetcher } from "@/modules/fetch/fetcher"
import { toCamelCaseKeys } from "@/modules/format/object/toCamelCaseKeys"
import { toSnakeCaseKeys } from "@/modules/format/object/toSnakeCaseKeys"

const WOOSMAP_LOCALITIES_AUTOCOMPLETE = "https://api.woosmap.com/address/autocomplete/json"
export const localitiesAutocompleteKey = "woosmap::localities::autocomplete"

type LocalitiesAutocomplete = {
  components?: string
  key: string
  language: I18nLocale
  query: string
  types?: WoosMapLocalityTypes[]
}

const filterLocalitiesByTypes = (
  localities: WoosMapLocalitiesAutocomplete,
  types: WoosMapLocalityTypes[]
): WoosMapLocalitiesAutocomplete => {
  return {
    predictions: localities.predictions.filter(prediction => {
      return types.includes(prediction.type)
    }),
  }
}

export const localitiesAutocomplete = ({
  key,
  language,
  query,
  types = ["country", "locality", "admin_level", "route", "house_number"],
}: LocalitiesAutocomplete): Promise<WoosMapLocalitiesAutocomplete> => {
  return fetcher(WOOSMAP_LOCALITIES_AUTOCOMPLETE, {
    searchParams: new URLSearchParams({
      input: query,
      key,
      language,
    }),
    serviceDomain: "EXTERNAL",
  })
    .then(response => {
      return getResultFromResponse<WoosMapLocalitiesAutocomplete>({
        transformResult: toCamelCaseKeys<WoosMapLocalitiesAutocomplete>,
      })(response)
    })
    .then(localities => {
      return filterLocalitiesByTypes(localities, types)
    })
    .catch(handleFetchError(WOOSMAP_LOCALITIES_AUTOCOMPLETE))
}

type LocalityDetails = {
  key: string
  publicId: string
}

const WOOSMAP_LOCALITIES_DETAILS = "https://api.woosmap.com/address/details/json"
export const localityDetailsKey = "woosmap::locality::details"

export const localityDetails = ({ key, publicId }: LocalityDetails): Promise<WoosMapLocalityDetailResult> => {
  return fetcher(WOOSMAP_LOCALITIES_DETAILS, {
    searchParams: new URLSearchParams(
      toSnakeCaseKeys({
        key,
        language: "en",
        publicId,
      })
    ),
    serviceDomain: "EXTERNAL",
  })
    .then(response => {
      return getResultFromResponse<WoosMapLocalityDetailResult>({
        transformResult: toCamelCaseKeys<WoosMapLocalityDetailResult>,
      })(response)
    })
    .catch(handleFetchError(WOOSMAP_LOCALITIES_DETAILS))
}
